import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DataContext from "../../../context/DataContext";
import PaginatorContext from "../../../context/PaginatorContext";
import { DocumentsTopNavbar } from "../../../components/documents/DocumentsTopNavbar";
import { COILineItemForm } from "../../../components/documents/coi/COILineItemForm";
import { COIResolveDialog } from "../../../components/documents/coi/COIResolveDialog";
import { COIStatusBadge } from "../../../components/documents/coi/COIStatusBadge";
import { COIUploadForm } from "../../../components/documents/coi/COIUploadForm";
import {
  calculateCOINewRuntime,
  formatCOIDate,
  formatCOIInteger,
} from "../../../components/documents/coi/coiFormatters";
import { DeleteEntryDialog } from "../../../components/documents/pump/DeleteEntryDialog";
import { PumpStatusBadge } from "../../../components/documents/pump/PumpStatusBadge";
import { ReportsAdmin } from "../reports/ReportsAdmin";
import { useFetch } from "../../../hooks/useFetch";
import { usePermissions } from "../../../hooks/usePermissions";
import { useRequest } from "../../../hooks/useRequest";
import { useToolsRunTrackedWells } from "../../../hooks/useToolsRunTrackedWells";
import {
  ClientProps,
  COILineItemProps,
  COIRecordType,
  PumpEntryStatus,
} from "../../../interfaces/interfaces";

const EXCLUDE_ERROR = "__exclude_error__";

interface COIResponse {
  success?: boolean;
  data: COILineItemProps[];
}

interface Props {
  clientMode?: boolean;
  assignedClientOptions?: ClientProps[];
  assignedClientId?: number | null;
  onAssignedClientChange?: (clientId: number | null) => void;
}

const filterLabelSx = {
  display: "block",
  mb: "5px",
  color: "#9CA1AB",
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".6px",
  lineHeight: 1.2,
  textTransform: "uppercase",
};

const documentFilterSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    bgcolor: "#FAFAF8",
    borderRadius: "9px",
    fontSize: 13,
    "& fieldset": { borderColor: "#E2E2DD" },
    "&:hover fieldset": { borderColor: "#C9C7BE" },
    "&.Mui-focused fieldset": {
      borderColor: "#ED8B00",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": { py: "8px" },
};

const reasonLabel = (reason?: string | null) =>
  reason?.replaceAll("_", " ") || "";

export const COIAdmin = ({
  clientMode = false,
  assignedClientOptions = [],
  assignedClientId = null,
  onAssignedClientChange,
}: Props) => {
  const { paginationModel, fetchPaginationModel } =
    useContext(PaginatorContext);
  const {
    clientOptions: globalClientOptions,
    fetchDataClient,
    fetchDataProducts,
  } = useContext(DataContext);
  const { handleRequest } = useRequest();
  const { wellOptions, fetchDataWells } = useToolsRunTrackedWells();
  const { canManageCOI } = usePermissions();
  const clientOptions = clientMode
    ? assignedClientOptions
    : globalClientOptions;
  const [recordType, setRecordType] = useState<COIRecordType>("inventory");
  const [showReports, setShowReports] = useState(false);
  const [clientId, setClientId] = useState("");
  const [wellId, setWellId] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [status, setStatus] = useState(EXCLUDE_ERROR);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [resolveRow, setResolveRow] = useState<COILineItemProps | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<COILineItemProps | null>(null);
  const [deletingRow, setDeletingRow] = useState<COILineItemProps | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!clientMode) fetchDataClient();
    fetchDataProducts();
    // Master data is intentionally loaded once per page mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode]);

  useEffect(() => {
    if (!clientMode || !assignedClientId) return;
    setClientId(String(assignedClientId));
    setWellId("");
    fetchDataWells(assignedClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedClientId, clientMode]);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setSearch(searchDraft.trim()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [searchDraft]);

  useEffect(() => {
    if (!clientId) {
      setCategories([]);
      return;
    }
    let active = true;
    handleRequest({
      endpoint: `coi-categories/client/${clientId}`,
      options: { method: "GET" },
    })
      .then((response) => {
        if (active) setCategories(response.data || []);
      })
      .catch(() => {
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
    // Request handler is stable enough for client-id scoped loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const filters = useMemo(
    () => ({
      recordType,
      clientId,
      wellId,
      search,
      category,
      status: status === EXCLUDE_ERROR ? "" : status,
      excludeStatus: status === EXCLUDE_ERROR ? "error" : "",
      dateFrom,
      dateTo,
    }),
    [
      recordType,
      clientId,
      wellId,
      search,
      category,
      status,
      dateFrom,
      dateTo,
    ],
  );
  const { data, isLoading, error, fetchData, pagination } =
    useFetch<COIResponse>(
      showReports ? undefined : "coi-paginate",
      paginationModel,
      undefined,
      undefined,
      filters,
    );
  const rows = data?.data || [];
  const resetPage = () =>
    fetchPaginationModel({ ...paginationModel, page: 0 });
  const chooseClient = (value: string) => {
    const nextClientId =
      clientMode && !value && assignedClientId
        ? String(assignedClientId)
        : value;
    setClientId(nextClientId);
    setWellId("");
    setCategory("");
    if (clientMode) {
      onAssignedClientChange?.(
        nextClientId ? Number(nextClientId) : null,
      );
    }
    if (nextClientId) fetchDataWells(Number(nextClientId));
    resetPage();
  };
  const filterText =
    (setter: (value: string) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setter(event.target.value);
      resetPage();
    };
  const hasActiveFilters = Boolean(
    wellId || searchDraft || category || status !== EXCLUDE_ERROR || dateFrom || dateTo,
  );
  const clearFilters = () => {
    setWellId("");
    setSearchDraft("");
    setSearch("");
    setCategory("");
    setStatus(EXCLUDE_ERROR);
    setDateFrom("");
    setDateTo("");
    resetPage();
  };

  const updateRowBusinessStatus = async (row: COILineItemProps, nextStatus: PumpEntryStatus | "") => {
    await handleRequest({
      endpoint: `coi/${row.id}`,
      options: { method: "PUT", body: JSON.stringify({ businessStatus: nextStatus || null }) },
    });
    fetchData();
  };

  const confirmDeleteRow = async () => {
    if (!deletingRow) return;
    setDeleting(true);
    try {
      await handleRequest({
        endpoint: `coi/${deletingRow.id}`,
        options: { method: "DELETE" },
      });
      setDeletingRow(null);
      fetchData();
    } finally {
      setDeleting(false);
    }
  };

  const tableHeaders =
    recordType === "inventory"
      ? [
          "P/N / Description",
          "Category",
          "Qty",
          "Install date",
          "Pull date",
          "Runtime",
          "Client / From",
          "To",
          "Match Status",
          "Business Status",
          "",
        ]
      : [
          "P/N / Description",
          "Category",
          "Qty",
          "Install date",
          "Pull date",
          "Client / From",
          "To",
          "Reinstall date",
          "New runtime",
          "Match Status",
          "Business Status",
          "",
        ];
  const colSpan = tableHeaders.length;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F3F3F0",
        color: "#29313D",
      }}
    >
      <DocumentsTopNavbar
        title="Customer Owned Inventory"
        actions={
          !showReports && canManageCOI ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={() => {
                  setEditingRow(null);
                  setFormOpen(true);
                }}
                sx={{
                  borderColor: "#E7E7E3",
                  color: "#2A2F38",
                  borderRadius: "9px",
                  px: "15px",
                  py: "8px",
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
                }}
              >
                Add Record
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFileRoundedIcon />}
                onClick={() => setUploadOpen(true)}
                sx={{
                  borderColor: "#E7E7E3",
                  color: "#2A2F38",
                  borderRadius: "9px",
                  px: "15px",
                  py: "8px",
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
                }}
              >
                Import Excel
              </Button>
            </Box>
          ) : undefined
        }
      />
      <Box sx={{ maxWidth: 1680, mx: "auto", p: { xs: 2, md: 3.5 } }}>
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            border: "1px solid #E7E7E3",
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={showReports ? "reports" : recordType}
            onChange={(_, value: COIRecordType | "reports") => {
              if (value === "reports") {
                setShowReports(true);
                resetPage();
                return;
              }
              setShowReports(false);
              setRecordType(value);
              setDateFrom("");
              setDateTo("");
              resetPage();
            }}
            sx={{
              px: 2,
              minHeight: 52,
              borderBottom: "1px solid #ECEBE6",
              "& .MuiTab-root": { minHeight: 52, fontSize: 12, fontWeight: 850 },
              "& .MuiTabs-indicator": { bgcolor: "#ED8B00", height: 3 },
            }}
          >
            <Tab value="inventory" label="Inventory" />
            <Tab value="reinstall" label="Reinstall" />
            <Tab value="reports" label="Dashboard" />
          </Tabs>
          {!showReports && (
            <Box
              sx={{
                p: "16px 20px",
                display: "flex",
                alignItems: "flex-end",
                gap: "11px",
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ width: { xs: "100%", sm: 190 } }}>
                <Box component="label" sx={filterLabelSx}>Client</Box>
                <Autocomplete
                  size="small"
                  options={clientOptions}
                  value={
                    clientOptions.find(
                      (client) => String(client.id) === clientId,
                    ) || null
                  }
                  getOptionLabel={(client) => client.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, client) =>
                    chooseClient(client ? String(client.id) : "")
                  }
                  disableClearable={clientMode && clientOptions.length === 1}
                  disabled={clientMode && clientOptions.length === 1}
                  autoHighlight
                  openOnFocus
                  sx={documentFilterSx}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Search clients…" />
                  )}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: 190 } }}>
                <Box component="label" sx={filterLabelSx}>From well</Box>
                <Autocomplete
                  size="small"
                  options={wellOptions}
                  value={
                    wellOptions.find((well) => String(well.id) === wellId) || null
                  }
                  getOptionLabel={(well) => well.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, well) => {
                    setWellId(well ? String(well.id) : "");
                    resetPage();
                  }}
                  disabled={!clientId}
                  autoHighlight
                  openOnFocus
                  sx={documentFilterSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={clientId ? "Search wells…" : "Select a client"}
                    />
                  )}
                />
              </Box>
              <Box sx={{ flex: "1 1 260px", minWidth: 230 }}>
                <Box component="label" sx={filterLabelSx}>Inventory search</Box>
                <TextField
                  fullWidth
                  size="small"
                  value={searchDraft}
                  onChange={filterText(setSearchDraft)}
                  placeholder="P/N, description or To…"
                  sx={documentFilterSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: "#A6ABB5", fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: 190 } }}>
                <Box component="label" sx={filterLabelSx}>Category</Box>
                <FormControl fullWidth size="small" sx={documentFilterSx}>
                  <Select
                    value={category}
                    displayEmpty
                    onChange={(event) => {
                      setCategory(event.target.value);
                      resetPage();
                    }}
                  >
                    <MenuItem value="">All categories</MenuItem>
                    {categories.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {!clientMode ? (
                <Box sx={{ width: { xs: "100%", sm: 145 } }}>
                  <Box component="label" sx={filterLabelSx}>Status</Box>
                  <FormControl fullWidth size="small" sx={documentFilterSx}>
                    <Select
                      value={status}
                      displayEmpty
                      onChange={(event) => {
                        setStatus(event.target.value);
                        resetPage();
                      }}
                    >
                      <MenuItem value={EXCLUDE_ERROR}>All (except errors)</MenuItem>
                      <MenuItem value="">All statuses</MenuItem>
                      <MenuItem value="matched">Matched</MenuItem>
                      <MenuItem value="needs_review">Needs review</MenuItem>
                      <MenuItem value="error">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ) : null}
              <Box sx={{ width: { xs: "100%", sm: 145 } }}>
                <Box component="label" sx={filterLabelSx}>
                  {recordType === "inventory" ? "Pull from" : "Reinstall from"}
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={dateFrom}
                  onChange={filterText(setDateFrom)}
                  sx={documentFilterSx}
                />
              </Box>
              <Box sx={{ width: { xs: "100%", sm: 145 } }}>
                <Box component="label" sx={filterLabelSx}>To date</Box>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={dateTo}
                  onChange={filterText(setDateTo)}
                  sx={documentFilterSx}
                />
              </Box>
              {hasActiveFilters ? (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={clearFilters}
                  sx={{
                    borderColor: "#E2E2DD",
                    borderRadius: "9px",
                    fontSize: 12.5,
                    fontWeight: 650,
                    textTransform: "none",
                  }}
                >
                  Clear filters
                </Button>
              ) : null}
            </Box>
          )}
        </Paper>

        {showReports ? (
          <ReportsAdmin
            embedded
            clientMode={clientMode}
            assignedClientOptions={assignedClientOptions}
            assignedClientId={assignedClientId}
            onAssignedClientChange={onAssignedClientChange}
          />
        ) : (
          <>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Customer Owned Inventory could not be loaded.
              </Alert>
            ) : null}
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #E4E3DC",
                borderRadius: 2.5,
                overflow: "hidden",
              }}
            >
              <TableContainer>
                <Table sx={{ minWidth: recordType === "inventory" ? 1320 : 1500 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#F8F8F5" }}>
                      {tableHeaders.map((label, index) => (
                        <TableCell
                          key={`${label}-${index}`}
                          align={label === "" ? "right" : "left"}
                          sx={{
                            color: "#707782",
                            fontSize: 11,
                            letterSpacing: 0.65,
                            textTransform: "uppercase",
                            fontWeight: 850,
                            borderColor: "#EAE9E3",
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={colSpan} align="center" sx={{ py: 8 }}>
                          <CircularProgress size={30} />
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {!isLoading && !rows.length ? (
                      <TableRow>
                        <TableCell colSpan={colSpan} align="center" sx={{ py: 8 }}>
                          <Inventory2OutlinedIcon sx={{ fontSize: 36, color: "#B8BDC5", mb: 1 }} />
                          <Typography fontWeight={800}>
                            No {recordType === "inventory" ? "inventory" : "reinstalled"} rows found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {clientMode
                              ? "Adjust the filters to find customer-owned tools."
                              : "Select a client, adjust filters, or import a workbook."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {!isLoading &&
                      rows.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{ "& td": { borderColor: "#EFEEE9" } }}
                        >
                          <TableCell sx={{ minWidth: 240 }}>
                            <Typography variant="body2" fontWeight={850}>
                              {row.partNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.product?.name || row.equipmentDescription || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.equipmentCategory || "—"}</TableCell>
                          <TableCell sx={{ fontWeight: 750 }}>
                            {formatCOIInteger(row.quantity)}
                          </TableCell>
                          <TableCell>{formatCOIDate(row.installDate)}</TableCell>
                          <TableCell>{formatCOIDate(row.pullDate)}</TableCell>
                          {recordType === "inventory" ? (
                            <>
                              <TableCell>{formatCOIInteger(row.runtimeDays)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={750}>
                                  {row.client?.name || "—"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.well?.name || row.fromWellRaw || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 220 }}>{row.toText || "—"}</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>
                                <Typography variant="body2" fontWeight={750}>
                                  {row.client?.name || "—"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {row.well?.name || row.fromWellRaw || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ maxWidth: 220 }}>{row.toText || "—"}</TableCell>
                              <TableCell>{formatCOIDate(row.reinstallDate)}</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>
                                {formatCOIInteger(
                                  calculateCOINewRuntime(row.reinstallDate),
                                )}{" "}
                                days
                              </TableCell>
                            </>
                          )}
                          <TableCell>
                            <COIStatusBadge status={row.status} />
                            {row.status !== "matched" ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5, textTransform: "capitalize" }}
                              >
                                {reasonLabel(row.errorReason || row.reviewReason)}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {row.businessStatus ? (
                                <PumpStatusBadge status={row.businessStatus} />
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                              {canManageCOI ? (
                                <FormControl size="small" sx={{ minWidth: 108 }}>
                                  <Select
                                    value={row.businessStatus || ""}
                                    displayEmpty
                                    onChange={(event) =>
                                      updateRowBusinessStatus(row, event.target.value as PumpEntryStatus | "")
                                    }
                                    sx={{ fontSize: 12 }}
                                  >
                                    <MenuItem value="">Not set</MenuItem>
                                    <MenuItem value="in-stock">In stock</MenuItem>
                                    <MenuItem value="reused">Reused</MenuItem>
                                  </Select>
                                </FormControl>
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                              {canManageCOI && row.status === "needs_review" ? (
                                <Button
                                  size="small"
                                  startIcon={<AutoFixHighRoundedIcon />}
                                  onClick={() => setResolveRow(row)}
                                  sx={{ color: "#A65F00", fontWeight: 800 }}
                                >
                                  Resolve
                                </Button>
                              ) : null}
                              {canManageCOI ? (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingRow(row);
                                    setFormOpen(true);
                                  }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : null}
                              {canManageCOI ? (
                                <IconButton size="small" onClick={() => setDeletingRow(row)}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              ) : null}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={Number(pagination.totalRecords || 0)}
                page={paginationModel.page}
                rowsPerPage={paginationModel.pageSize}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(_, page) =>
                  fetchPaginationModel({ ...paginationModel, page })
                }
                onRowsPerPageChange={(event) =>
                  fetchPaginationModel({
                    page: 0,
                    pageSize: Number(event.target.value),
                  })
                }
              />
            </Paper>
          </>
        )}
      </Box>
      <COIUploadForm
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(result) => {
          const importedClientId = result.clientId;
          if (importedClientId) {
            setClientId(String(importedClientId));
            fetchDataWells(importedClientId);
          }
          setRecordType("inventory");
          clearFilters();
          fetchData();
          setResolveRow(
            result.rows.find((row) => row.status === "needs_review") || null,
          );
        }}
      />
      <COIResolveDialog
        row={resolveRow}
        onClose={() => setResolveRow(null)}
        onResolved={fetchData}
      />
      <COILineItemForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingRow(null);
        }}
        onSaved={fetchData}
        clientOptions={clientOptions}
        categories={categories}
        initialClientId={clientId ? Number(clientId) : null}
        lockedClientId={clientMode ? assignedClientId : null}
        editingRow={editingRow}
      />
      <DeleteEntryDialog
        open={Boolean(deletingRow)}
        onClose={() => setDeletingRow(null)}
        onConfirm={confirmDeleteRow}
        confirming={deleting}
        title="Delete inventory record"
        description="This will permanently remove this inventory record. This action cannot be undone."
      />
    </Box>
  );
};
